// JapaneseRestaurantFooter.jsx
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styles from "./footer.module.css";
import {
  address,
  email,
  googleMapsLink,
  hours,
  phone,
  sakuraIcon,
} from "../Header/Header";
import { FaClock, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import { QRCodeCanvas } from "qrcode.react";
import { MdPets, MdNotInterested } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

export default function Footer() {
  const footerRef = useRef(null);
  const waveRef = useRef(null);
  const sakuraRefs = useRef([]);
  const contentRef = useRef(null);
  const logoRef = useRef(null);
  const infoItemsRef = useRef([]);
  const thanksRef = useRef(null);
  const bottomBarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Footer entrance animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: footerRef.current,
          start: "top 80%",
          end: "bottom bottom",
          toggleActions: "play none none reverse",
        },
      });

      // Background wave animation
      tl.fromTo(
        waveRef.current,
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power2.out" },
      )
        // Logo animation
        .fromTo(
          logoRef.current,
          { scale: 0, rotation: -180, opacity: 0 },
          {
            scale: 1,
            rotation: 0,
            opacity: 1,
            duration: 1,
            ease: "back.out(1.7)",
          },
          "-=0.8",
        )
        // Content fade in
        .fromTo(
          contentRef.current,
          { y: 50, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "power2.out" },
          "-=0.6",
        )
        // Info items stagger animation
        .fromTo(
          infoItemsRef.current,
          { y: 30, opacity: 0, scale: 0.9 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
            stagger: 0.2,
            ease: "power2.out",
          },
          "-=0.4",
        )
        // Thanks section animation
        .fromTo(
          thanksRef.current,
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "power2.out" },
          "-=0.3",
        )
        // Bottom bar animation
        .fromTo(
          bottomBarRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
          "-=0.2",
        );

      // Continuous wave animation
      gsap.to(waveRef.current, {
        x: "-=100",
        duration: 15,
        repeat: -1,
        ease: "none",
      });

      // Enhanced sakura falling animation
      sakuraRefs.current.forEach((sakura, i) => {
        gsap.fromTo(
          sakura,
          {
            y: -150,
            x: gsap.utils.random(-300, 300),
            rotation: gsap.utils.random(-180, 180),
            opacity: 0,
            scale: 0.5,
          },
          {
            y: window.innerHeight + 150,
            opacity: [0, 1, 1, 0],
            rotation: gsap.utils.random(-720, 720),
            scale: [0.5, 1, 1, 0.8],
            duration: gsap.utils.random(10, 18),
            ease: "none",
            repeat: -1,
            delay: i * 1.5,
            repeatDelay: gsap.utils.random(2, 8),
          },
        );
      });

      // Hover animations for interactive elements
      const infoItems = infoItemsRef.current;
      infoItems.forEach((item) => {
        if (item) {
          item.addEventListener("mouseenter", () => {
            gsap.to(item, { scale: 1.05, duration: 0.3, ease: "power2.out" });
          });
          item.addEventListener("mouseleave", () => {
            gsap.to(item, { scale: 1, duration: 0.3, ease: "power2.out" });
          });
        }
      });
    }, footerRef);

    return () => ctx.revert();
  }, [location.pathname]);

  const logo = "/assets/logo/logo.png";
  return (
    <>
      <iframe
        className={styles.iframeAddress}
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2508.5624005966642!2d13.692183199999999!3d51.0427025!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4709c57cbb01d1d9%3A0xb208158833c96fb!2sXXL%20Restaurant%20MaxiMahl%20Dresden!5e0!3m2!1svi!2s!4v1767933036208!5m2!1svi!2s"
        width="600"
        height="450"
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>

      <footer ref={footerRef} className={styles.footer}>
        <div className={styles.noDogsContainer}>
          <div className={styles.noDogsIconContainer}>
            <div className={styles.noDogsIcon}>
              <MdPets className={styles.dogIcon} />
              <MdNotInterested className={styles.banIcon} />
            </div>
          </div>
          <div className={styles.noDogsTitle}>Hinweis</div>
          <div className={styles.infoLink}>Hunde nicht erlaubt</div>
          <div className={styles.infoLinkSmall}>
            Wir bitten um Ihr Verständnis
          </div>
        </div>
        {/* Enhanced Sakura particles */}
        <div className={styles.sakuraContainer}>
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              ref={(el) => (sakuraRefs.current[i] = el)}
              className={styles.sakura}
            >
              <img src={sakuraIcon} height="40px" alt="" />
            </div>
          ))}
        </div>

        {/* Enhanced wave background */}
        <div ref={waveRef} className={styles.waveWrapper}>
          <div className={styles.wave}></div>
          <div className={styles.wave2}></div>
          <div className={styles.wave3}></div>
        </div>

        <div ref={contentRef} className={styles.content}>
          {/* Logo Section */}
          <div className={styles.logoSection}>
            <div ref={logoRef} className={styles.logo}>
              <img src={logo} alt="GIRI Logo" />
              <div className={styles.logoGlow}></div>
            </div>
            <p className={styles.tagline}>Authentic Asian Fusion Cuisine</p>
            <div className={styles.divider}></div>
          </div>

          {/* Info Grid */}
          <div className={styles.infoGrid}>
            <div
              ref={(el) => (infoItemsRef.current[0] = el)}
              className={styles.infoItem}
            >
              <div className={styles.iconWrapper}>
                <FaMapMarkerAlt className={styles.icon} />
              </div>
              <h4>Standort</h4>
              <a
                href={googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.infoLink}
              >
                {address}
              </a>
            </div>

            <div
              ref={(el) => (infoItemsRef.current[1] = el)}
              className={styles.infoItem}
            >
              <div className={styles.iconWrapper}>
                <FaClock className={styles.icon} />
              </div>
              <h4>Öffnungszeiten</h4>
              <div className={styles.infoLink}>{hours}</div>
              <div className={styles.infoLinkSmall}>
                Küchenschluss: 21:30 Uhr
              </div>
            </div>

            <div
              ref={(el) => (infoItemsRef.current[2] = el)}
              className={styles.infoItem}
            >
              <div className={styles.iconWrapper}>
                <FaPhoneAlt className={styles.icon} />
              </div>
              <h4>Kontakt</h4>
              <a href={`tel:${phone}`} className={styles.infoLink}>
                {phone}
              </a>
              <a href={`mailto:${email}`} className={styles.infoLink}>
                {email}
              </a>
            </div>
          </div>

          {/* Thanks Section */}
          <div ref={thanksRef} className={styles.thanksSection}>
            <h4 className={styles.thanksTitle}>
              <span className={styles.japaneseText}>Herzlichen Dank</span>
              Vielen Dank für Ihren Besuch!
            </h4>
            <p className={styles.thanksText}>
              Es ist uns eine besondere Ehre, Sie im GIRI willkommen zu heißen.
              Jedes unserer Gerichte spiegelt unsere Leidenschaft sowie unseren
              tiefen Respekt für die kulinarischen Traditionen Asiens wider.
            </p>

            <div className={styles.newsletterBox}>
              <p className={styles.newsletterTitle}>
                <span className={styles.japaneseText}>
                  Bleiben Sie verbunden
                </span>
                Bleiben Sie verbunden
              </p>
              <p>
                Abonnieren Sie unseren Newsletter für exklusive Angebote und
                Neuigkeiten.
              </p>
              <div className={styles.subscribeInputWrapper}>
                <button
                  className={styles.subscribeBtn}
                  onClick={() => {
                    navigate("/kontakt");
                    window.scrollTo(0, 0);
                  }}
                >
                  Kontakt aufnehmen
                </button>
              </div>
              {/* QR Code Section */}
              <div className={styles.qrSection}>
                <h3 className={styles.qrTitle}>Besuchen Sie unsere Website</h3>
                <p className={styles.qrDescription}>
                  Scannen Sie den QR-Code um unsere Website zu besuchen
                </p>
                <div className={styles.qrCodeWrapper}>
                  <QRCodeCanvas
                    value="https://giri-dresden.de/"
                    size={150}
                    level="M"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#333333"
                  />
                </div>
                <a
                  href="https://giri-dresden.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.websiteLink}
                >
                  www.giri-dresden.de
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div ref={bottomBarRef} className={styles.bottomBar}>
            <p> 2026 GIRI Restaurant - Asian Fusion Cuisine</p>
            <div className={styles.social}>
              <a href="/Impressum">Impressum</a>
              <span className={styles.separator}>•</span>
              <a href="/Datenschutz">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
